/**
 * XRModule — IModule implementation for WebXR session lifecycle.
 *
 * Registered via app.use(xrModule). Handles capability detection,
 * session request/end, renderer configuration, and system registration.
 */

import type { App, IModule } from '../core/App.js';
import { Logger } from '../core/Logger.js';
import {
  XRSessionState,
  DEFAULT_XR_CONFIG,
  type XRConfig,
  type XRCapabilities,
  type XRSessionModeString,
} from './types.js';
import { XRCameraOverrideSystem } from './systems/XRCameraOverrideSystem.js';
import { XRInputPollSystem } from './systems/XRInputPollSystem.js';
import { XRHitTestSystem } from './systems/XRHitTestSystem.js';
import { XRAnchorSystem } from './systems/XRAnchorSystem.js';
import { XRDepthSystem } from './systems/XRDepthSystem.js';
import { XRPlaneSystem } from './systems/XRPlaneSystem.js';
import { XRMeshSystem } from './systems/XRMeshSystem.js';
import { XRLightProbeSystem } from './systems/XRLightProbeSystem.js';
import { createXRInputContext, removeXRInputContext } from './XRInputContext.js';
import {
  setupControllerEntities,
  teardownControllerEntities,
} from './XRControllerEntities.js';
import { XRMultiviewDetector } from './XRMultiviewDetector.js';
import { XRLayerManager } from './layers/XRLayerManager.js';
import { XRLayerSystem } from './layers/XRLayerSystem.js';
import { XRPerfMonitorSystem } from './XRPerfMonitorSystem.js';
import { XRHapticsManager } from './XRHapticsManager.js';
import { requestImmersiveSession } from './XRSessionNegotiator.js';
import { XRWebGLPresentationBackend } from './presentation/XRWebGLPresentationBackend.js';
import { XRWebGPUPresentationBackend } from './presentation/XRWebGPUPresentationBackend.js';
import type { IXRPresentationBackend } from './presentation/IXRPresentationBackend.js';
import { createDefaultRuntimeCaps, createXrCaps, type RuntimeCaps } from './XRCapabilities2.js';
import { RendererRouter } from '../render/RendererRouter.js';
import { applyUniformArrayNodePatch, applyFoveationNullGuardPatch, applyDrawBuffersNullGuardPatch, applyPixelRatioXRGuardPatch } from './patches/UniformArrayNodePatch.js';

/**
 * Pick the display rate: 'max' → highest supported; number → highest
 * supported rate that does not exceed it (falls back to the LOWEST
 * supported rate when the target is below all of them).
 */
export function pickFrameRate(rates: number[], target: number | 'max'): number | undefined {
  if (rates.length === 0) return undefined;
  const sorted = [...rates].sort((a, b) => a - b);
  if (target === 'max') return sorted[sorted.length - 1];
  const eligible = sorted.filter((r) => r <= target);
  return eligible.length > 0 ? eligible[eligible.length - 1] : sorted[0];
}

export class XRModule implements IModule {
  readonly name = 'XR';

  private app!: App;
  private _config: XRConfig;
  private _capabilities: XRCapabilities = {
    webxrAvailable: false,
    vrSupported: false,
    arSupported: false,
    inlineSupported: false,
  };
  private _state = new XRSessionState();

  // New subsystems
  private _multiview = new XRMultiviewDetector();
  private _layerManager = new XRLayerManager();
  private _haptics!: XRHapticsManager;
  private _presentationBackend: IXRPresentationBackend | null = null;
  private _runtimeCaps: RuntimeCaps;
  private _rendererRouter = new RendererRouter();
  private _disposed = false;

  // Bound handlers for cleanup
  private _onSessionEnd: (() => void) | null = null;
  private _onVisibilityChange: (() => void) | null = null;
  private _onInputSourcesChange: ((e: XRInputSourcesChangeEvent) => void) | null = null;

  // Saved scene state for restoration after AR session
  private _savedSceneBackground: any = undefined;

  constructor(config?: Partial<XRConfig>) {
    this._config = { ...DEFAULT_XR_CONFIG, ...config };
    this._runtimeCaps = createDefaultRuntimeCaps('webgpu'); // Will be updated in init
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  get config(): Readonly<XRConfig> {
    return this._config;
  }

  get capabilities(): XRCapabilities {
    return this._capabilities;
  }

  get isPresenting(): boolean {
    return this._state.isPresenting;
  }

  get currentMode(): XRSessionModeString | null {
    return this._state.mode;
  }

  /**
   * Merge config changes for FUTURE session requests (feature lists, display
   * rate, foveation, …). Does not touch an already-running session.
   */
  updateConfig(partial: Partial<XRConfig>): void {
    this._config = { ...this._config, ...partial };
  }

  get state(): XRSessionState {
    return this._state;
  }

  get multiview(): XRMultiviewDetector {
    return this._multiview;
  }

  get layerManager(): XRLayerManager {
    return this._layerManager;
  }

  get haptics(): XRHapticsManager {
    return this._haptics;
  }

  get presentationBackend(): IXRPresentationBackend | null {
    return this._presentationBackend;
  }

  get runtimeCaps(): RuntimeCaps {
    return this._runtimeCaps;
  }

  get rendererRouter(): RendererRouter {
    return this._rendererRouter;
  }

  /** Debug metrics for DevTools panel. */
  get debugMetrics(): {
    gfxBackend: string;
    xrBinding: string;
    supportsLayers: boolean;
    depthRange: string;
    routerState: string;
  } {
    return {
      gfxBackend: this._runtimeCaps.gfx,
      xrBinding: this._presentationBackend?.binding ?? 'off',
      supportsLayers: this._presentationBackend?.supportsLayers ?? false,
      depthRange: this._state.depthRange,
      routerState: this._rendererRouter.state,
    };
  }

  // ─── IModule Lifecycle ──────────────────────────────────────────────────────

  async init(app: App): Promise<void> {
    this.app = app;

    // r185 XR crash guard: three's own `cameraPositions` uniform array throws
    // during every lit-material build under an ArrayCamera, and NodeManager
    // silently swaps the material for a BLANK NodeMaterial — the whole lit
    // world vanishes in the headset. Mirrors the unreleased upstream fix.
    applyUniformArrayNodePatch();
    // r185 second XR crash: foveateBoundTexture(null) throws EVERY session
    // frame (aborting controller updates + the app loop) whenever no output
    // post target exists — the stale/head-lagged eye-layer field bug.
    applyFoveationNullGuardPatch(app.renderer.native.xr);
    // r185 third XR crash (WebGL backend): drawBuffers WeakMap.set(null) on a
    // null base-layer framebuffer (inline/emulated sessions) kills every tick.
    applyDrawBuffersNullGuardPatch((app.renderer.native as unknown as { backend?: unknown }).backend);
    // r185 fourth XR bug — THE eye-misalignment root cause: CanvasTarget's
    // XR guard is dead (this.xr never wired) and setPixelRatio half-applies
    // mid-session; a dpr-0.75 device (Quest browser) then renders into 3/4
    // of the layer with overlapping eyes.
    applyPixelRatioXRGuardPatch(app.renderer.native);

    // Detect capabilities
    await this._detectCapabilities();

    // Update runtime caps based on renderer type
    this._runtimeCaps = createDefaultRuntimeCaps(
      app.renderer.isWebGPU ? 'webgpu' : 'webgl2'
    );

    // Register XR systems
    const state = this._state;
    const renderer = app.renderer;

    app.world.addSystem(new XRCameraOverrideSystem(state, renderer));
    app.world.addSystem(new XRInputPollSystem(state, renderer));
    app.world.addSystem(new XRHitTestSystem(state, app.events));
    app.world.addSystem(new XRAnchorSystem(state, app.events));
    app.world.addSystem(new XRDepthSystem(state, renderer));
    app.world.addSystem(new XRPlaneSystem(state, app.events));
    app.world.addSystem(new XRMeshSystem(state, app.events));
    app.world.addSystem(new XRLightProbeSystem(state, app.world.scene));

    // XR Layers system
    app.world.addSystem(new XRLayerSystem(state, this._layerManager, renderer));

    // XR Perf Monitor
    app.world.addSystem(new XRPerfMonitorSystem(state, renderer, app.events));

    // Haptics manager
    this._haptics = new XRHapticsManager(state);

    // Detect multiview support
    this._multiview.detect(renderer.native);
    if (this._multiview.available) {
      this._multiview.enable(renderer.native);
    }

    // Handle context loss on canvas
    const canvas = renderer.native.domElement;
    canvas.addEventListener('webglcontextlost', this._onContextLost);
    canvas.addEventListener('webglcontextrestored', this._onContextRestored);

    Logger.info('XR', `Initialized — VR:${this._capabilities.vrSupported} AR:${this._capabilities.arSupported}`);
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    // dispose() before init() (app.start() rejected on an earlier module):
    // nothing was registered, and `this.app` is unset — the canvas-listener
    // removal below would throw a TypeError out of the app's teardown path.
    if (!this.app) return;
    if (this._state.isPresenting) {
      // Capture session ref before _handleSessionEnd → _state.reset() nulls it.
      const session = this._state.session;
      // Run session teardown logic synchronously before the async end().
      // _handleSessionEnd() would normally fire from the 'end' event, but
      // since dispose() is synchronous we must invoke the cleanup directly
      // to avoid skipping controller teardown, input context removal, and
      // presentation backend detach.
      this._handleSessionEnd();
      // Fire the async end — the 'end' listener was already removed by
      // _handleSessionEnd → _cleanupSession, so it won't double-fire.
      session?.end().catch(() => {});
    } else {
      // Only call _cleanupSession directly if not presenting, since
      // _handleSessionEnd already calls it internally
      this._cleanupSession();
    }

    const canvas = this.app.renderer.native.domElement;
    canvas.removeEventListener('webglcontextlost', this._onContextLost);
    canvas.removeEventListener('webglcontextrestored', this._onContextRestored);
  }

  // ─── Session Management ─────────────────────────────────────────────────────

  /**
   * Request an XR session. Must be called from a user gesture handler.
   */
  async requestSession(mode?: XRSessionModeString): Promise<void> {
    const sessionMode = mode ?? this._config.defaultMode;

    if (this._state.isPresenting) {
      Logger.warn('XR', 'Session already active, ending current session first');
      await this.endSession();
    }

    // Use the negotiator for immersive sessions
    if (sessionMode === 'immersive-vr' || sessionMode === 'immersive-ar') {
      const result = await requestImmersiveSession(sessionMode, {
        allowWebGpuXr: this._config.allowWebGpuXr ?? false,
        requiredFeatures: this._config.requiredFeatures,
        optionalFeatures: this._config.optionalFeatures,
        useLayersApi: this._config.useLayersApi,
        domOverlay: this._config.domOverlayElement
          ? { root: this._config.domOverlayElement }
          : undefined,
        depthSensing:
          this._config.depthSensing ??
          (sessionMode === 'immersive-ar'
            ? { usagePreference: ['gpu-optimized', 'cpu-optimized'], dataFormatPreference: ['float32', 'luminance-alpha'] }
            : undefined),
      });

      const session = result.session;

      // Update renderer router
      this._rendererRouter.enterXR(result.binding);

      // Create presentation backend
      if (result.binding === 'webgpu' && result.gpuDevice) {
        this._presentationBackend = new XRWebGPUPresentationBackend(result.gpuDevice);
      } else {
        this._presentationBackend = new XRWebGLPresentationBackend(this.app.renderer);
      }

      // Attach session to presentation backend
      // For WebGL: this is done AFTER renderer.xr.setSession below
      // For WebGPU: attach now (it sets up layers)
      if (result.binding === 'webgpu') {
        try {
          await this._presentationBackend.attachSession(session);
        } catch (err) {
          // The experimental WebGPU XR binding (XRGPUBinding) is not yet
          // available in any production browser. Without this guard the
          // throw escapes requestSession() with the renderer router stuck
          // in XR mode, the session leaked, and presentationBackend
          // pointing at a half-initialized backend — leaving the engine
          // permanently in a broken state until a full restart.
          Logger.warn('XR', `WebGPU presentation backend attach failed: ${err}. Cleaning up.`);
          try { await this._presentationBackend.detachSession(); } catch { /* ignore */ }
          this._presentationBackend = null;
          this._rendererRouter.exitXR();
          try { await session.end(); } catch { /* ignore */ }
          throw err;
        }
      }

      // Configure renderer BEFORE setSession (critical ordering)
      const renderer = this.app.renderer.native;
      // Re-attempt the backend-state patch: at module init the WebGL backend
      // may not have constructed its state object yet (idempotent no-op once
      // applied).
      applyDrawBuffersNullGuardPatch((renderer as unknown as { backend?: unknown }).backend);
      renderer.xr.enabled = true;
      renderer.xr.setReferenceSpaceType(this._config.referenceSpaceType);
      renderer.xr.setFoveation(this._config.foveation ?? 1);
      renderer.xr.setFramebufferScaleFactor(this._config.framebufferScaleFactor ?? 1);
      if (this._config.useLayersApi === false) {
        // r185 has no public opt-out: _supportsLayers is derived from API
        // presence at construction and routes setSession to the
        // XRProjectionLayer path (external-texture MSAA resolve). Forcing
        // false here (before setSession — the only decision point) selects
        // the classic XRWebGLLayer path instead. Private-field poke by
        // necessity; re-check on every three upgrade.
        (renderer.xr as unknown as { _supportsLayers?: boolean })._supportsLayers = false;
      }

      // Initialize XR layer manager (WebGL path)
      if (result.binding === 'webgl') {
        const gl = (renderer as any).getContext?.();
        if (gl) {
          this._layerManager.init(session, gl);
        }
      }

      // Set up session event handlers
      this._onSessionEnd = () => this._handleSessionEnd();
      this._onVisibilityChange = () => this._handleVisibilityChange();
      this._onInputSourcesChange = (e: any) => this._handleInputSourcesChange(e as XRInputSourcesChangeEvent);

      session.addEventListener('end', this._onSessionEnd);
      session.addEventListener('visibilitychange', this._onVisibilityChange);
      session.addEventListener('inputsourceschange', this._onInputSourcesChange as EventListener);

      // Display rate (Quest 72/90/120): best-effort, after the session exists.
      this._applyTargetFrameRate(session);

      // Three.js sessionstart listener
      const onSessionStart = () => {
        renderer.xr.removeEventListener('sessionstart', onSessionStart);

        this._state.referenceSpace = renderer.xr.getReferenceSpace();
        this._state.enabledFeatures = (session as any).enabledFeatures ?? [];
        this._state.mode = sessionMode;

        // DOM overlay
        if ((session as any).domOverlayState?.type) {
          this._state.domOverlayType = (session as any).domOverlayState.type;
        }

        // AR background — save and clear for camera passthrough
        if (sessionMode === 'immersive-ar') {
          const blendMode = renderer.xr.getEnvironmentBlendMode();
          if (blendMode !== 'opaque') {
            this._savedSceneBackground = this.app.world.scene.background;
            this.app.world.scene.background = null;
          }
        }

        // Attach WebGL backend AFTER setSession (layer detection needs it)
        if (result.binding === 'webgl' && this._presentationBackend) {
          this._presentationBackend.attachSession(session).then(() => {
            (this._presentationBackend as XRWebGLPresentationBackend).detectLayerSupport();
          }).catch((err) => {
            Logger.warn('XR', `WebGL backend attach failed: ${err}`);
          });
        }

        // Set depth range on state from presentation backend
        this._state.depthRange = this._presentationBackend?.depthRange ?? 'neg1to1';

        // Update runtime caps with XR info
        this._runtimeCaps = {
          gfx: this.app.renderer.isWebGPU ? 'webgpu' : 'webgl2',
          xr: createXrCaps(result.binding, {
            supportsLayers: this._presentationBackend?.supportsLayers ?? false,
          }),
          postFXAllowed: false, // Default: no post-FX in XR
        };

        // Push XR input context
        createXRInputContext(this.app);

        // Set up controller entities
        setupControllerEntities(this.app, this._state, this.app.renderer);

        Logger.info('XR', `Session started: ${sessionMode} (binding: ${result.binding}, features: ${this._state.enabledFeatures.join(', ')})`);
        this.app.events.emit('xr:session-started', {
          mode: sessionMode,
          binding: result.binding,
          supportsLayers: this._presentationBackend?.supportsLayers ?? false,
          depthRange: this._presentationBackend?.depthRange ?? 'neg1to1',
        });
      };

      renderer.xr.addEventListener('sessionstart', onSessionStart);

      // Store session state
      this._state.session = session;

      // For WebGL binding: let Three.js handle session via setSession
      // For WebGPU binding: we handle rendering ourselves (experimental)
      if (result.binding === 'webgl') {
        try {
          await renderer.xr.setSession(session);
        } catch (err) {
          // NAME the failure with its stack: a silent rollback reads as a
          // "black flash then nothing" on a devtools-less headset — the
          // beacon must carry the actual throw.
          Logger.error('XR', `setSession failed — rolling back: ${(err as Error)?.stack ?? err}`);
          // Full rollback: undo all setup done above
          renderer.xr.removeEventListener('sessionstart', onSessionStart);
          this._cleanupSession();       // removes end/visibility/inputsources listeners
          this._state.session = null;
          this._state.mode = null;
          renderer.xr.enabled = false;
          if (this._presentationBackend) {
            this._presentationBackend.detachSession().catch(() => {});
            this._presentationBackend = null;
          }
          this._rendererRouter.exitXR();
          session.end().catch(() => {}); // release the XR session itself
          throw err;
        }
      } else {
        // WebGPU path: Three.js setSession is NOT called, so the 'sessionstart'
        // event never fires. We must initialize all session state manually.
        renderer.xr.removeEventListener('sessionstart', onSessionStart);

        // Full rollback if post-attach setup throws (e.g. requestReferenceSpace
        // rejects). Without this the session stays alive, the renderer router is
        // stuck in XR, the backend stays attached, and the end/visibility/
        // inputsources listeners (registered above) leak — mirroring the WebGL
        // path's rollback so a failed WebGPU start leaves the engine clean.
        try {
          this._state.referenceSpace = await session.requestReferenceSpace(
            this._config.referenceSpaceType,
          );
          this._state.enabledFeatures = (session as any).enabledFeatures ?? [];
          this._state.mode = sessionMode;

          // DOM overlay
          if ((session as any).domOverlayState?.type) {
            this._state.domOverlayType = (session as any).domOverlayState.type;
          }

          // AR background — save and clear for camera passthrough
          if (sessionMode === 'immersive-ar') {
            this._savedSceneBackground = this.app.world.scene.background;
            this.app.world.scene.background = null;
          }

          // Depth range from presentation backend
          this._state.depthRange = this._presentationBackend?.depthRange ?? '0to1';

          // Update runtime caps with XR info
          this._runtimeCaps = {
            gfx: 'webgpu',
            xr: createXrCaps('webgpu', {
              supportsLayers: this._presentationBackend?.supportsLayers ?? true,
            }),
            postFXAllowed: false,
          };

          // Push XR input context
          createXRInputContext(this.app);

          // Set up controller entities
          setupControllerEntities(this.app, this._state, this.app.renderer);

          Logger.info('XR', `Session started: ${sessionMode} (binding: webgpu, features: ${this._state.enabledFeatures.join(', ')})`);
          this.app.events.emit('xr:session-started', {
            mode: sessionMode,
            binding: 'webgpu' as const,
            supportsLayers: true,
            depthRange: this._presentationBackend?.depthRange ?? '0to1',
          });
        } catch (err) {
          this._cleanupSession();       // removes end/visibility/inputsources listeners
          this._state.session = null;
          this._state.mode = null;
          this._state.referenceSpace = null;
          // Restore the scene background if AR setup cleared it before throwing.
          if (this._savedSceneBackground !== undefined) {
            this.app.world.scene.background = this._savedSceneBackground;
            this._savedSceneBackground = undefined;
          }
          renderer.xr.enabled = false;
          if (this._presentationBackend) {
            this._presentationBackend.detachSession().catch(() => {});
            this._presentationBackend = null;
          }
          this._rendererRouter.exitXR();
          session.end().catch(() => {}); // release the XR session itself
          throw err;
        }
      }
    } else {
      // Inline session — simple path, no negotiation needed
      const xr = navigator.xr;
      if (!xr) throw new Error('WebXR not available');
      const session = await xr.requestSession(sessionMode);

      const renderer = this.app.renderer.native;
      renderer.xr.enabled = true;

      // r182 requires setReferenceSpaceType/setFoveation/setFramebufferScaleFactor
      // BEFORE setSession. The immersive path above does this correctly; mirror it here.
      renderer.xr.setReferenceSpaceType(this._config.referenceSpaceType);
      renderer.xr.setFoveation(this._config.foveation ?? 1);
      renderer.xr.setFramebufferScaleFactor(this._config.framebufferScaleFactor ?? 1);

      this._onSessionEnd = () => this._handleSessionEnd();
      session.addEventListener('end', this._onSessionEnd);

      this._state.session = session;
      this._state.mode = sessionMode;

      try {
        await renderer.xr.setSession(session);
        // The immersive paths set this; the inline path never did, so every
        // frame-data system (hit-test, planes, anchors, input poll) early-
        // returned on `!refSpace` with no diagnostic.
        this._state.referenceSpace = renderer.xr.getReferenceSpace();
      } catch (err) {
        // Rollback inline session setup
        if (this._onSessionEnd) session.removeEventListener('end', this._onSessionEnd);
        this._onSessionEnd = null;
        this._state.session = null;
        this._state.mode = null;
        renderer.xr.enabled = false;
        session.end().catch(() => {});
        throw err;
      }

      this.app.events.emit('xr:session-started', {
        mode: sessionMode,
        binding: 'webgl' as const,
        supportsLayers: false,
        depthRange: 'neg1to1' as const,
      });
    }
  }

  /**
   * End the active XR session.
   */
  private _ending = false;

  async endSession(): Promise<void> {
    if (!this._state.session || this._ending) return;
    // Guard against concurrent endSession() calls (e.g. rapid button tap).
    this._ending = true;
    try {
      await this._state.session.end();
      // _handleSessionEnd fires via 'end' event
    } finally {
      this._ending = false;
    }
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  private async _detectCapabilities(): Promise<void> {
    const xr = navigator.xr;
    if (!xr) {
      this._capabilities = {
        webxrAvailable: false,
        vrSupported: false,
        arSupported: false,
        inlineSupported: false,
      };
      Logger.warn('XR', 'WebXR not available');
      return;
    }

    const [vr, ar, inline] = await Promise.all([
      xr.isSessionSupported('immersive-vr').catch(() => false),
      xr.isSessionSupported('immersive-ar').catch(() => false),
      xr.isSessionSupported('inline').catch(() => false),
    ]);

    this._capabilities = {
      webxrAvailable: true,
      vrSupported: vr,
      arSupported: ar,
      inlineSupported: inline,
    };
  }

  /** Apply the configured display rate (no-op where unsupported). */
  private _applyTargetFrameRate(session: XRSession): void {
    const target = this._config.targetFrameRate;
    if (target === undefined || !session.updateTargetFrameRate) return;
    const rates = session.supportedFrameRates ? [...session.supportedFrameRates] : [];
    const pick = pickFrameRate(rates, target);
    if (pick === undefined) return;
    session.updateTargetFrameRate(pick).then(
      () => Logger.info('XR', `Display rate set to ${pick} Hz`),
      (err) => Logger.warn('XR', `updateTargetFrameRate(${pick}) failed: ${err}`),
    );
  }

  // ─── Persistent anchors (Quest) ─────────────────────────────────────────

  /**
   * Persist an anchor across sessions (Quest). Returns the UUID handle to
   * store (e.g. localStorage), or null where unsupported.
   */
  async persistAnchor(anchor: XRAnchor): Promise<string | null> {
    if (!anchor.requestPersistentHandle) return null;
    try {
      return await anchor.requestPersistentHandle();
    } catch (err) {
      Logger.warn('XR', `persistAnchor failed: ${err}`);
      return null;
    }
  }

  /** Restore a previously persisted anchor by UUID (null when gone/unsupported). */
  async restorePersistentAnchor(uuid: string): Promise<XRAnchor | null> {
    const session = this._state.session;
    if (!session?.restorePersistentAnchor) return null;
    try {
      return await session.restorePersistentAnchor(uuid);
    } catch (err) {
      Logger.warn('XR', `restorePersistentAnchor(${uuid}) failed: ${err}`);
      return null;
    }
  }

  /** Forget a persisted anchor (true when the UA accepted the delete). */
  async deletePersistentAnchor(uuid: string): Promise<boolean> {
    const session = this._state.session;
    if (!session?.deletePersistentAnchor) return false;
    try {
      await session.deletePersistentAnchor(uuid);
      return true;
    } catch (err) {
      Logger.warn('XR', `deletePersistentAnchor(${uuid}) failed: ${err}`);
      return false;
    }
  }

  /** UUIDs the UA still remembers for this origin (empty when unsupported). */
  get persistentAnchorHandles(): readonly string[] {
    return this._state.session?.persistentAnchors ?? [];
  }

  private _handleSessionEnd(): void {
    const prevMode = this._state.mode;
    Logger.info('XR', `Session ended (was: ${prevMode})`);

    // Teardown controller entities
    teardownControllerEntities(this.app, this._state);

    // Remove XR input context
    removeXRInputContext(this.app);

    // Cancel hit test source
    if (this._state.hitTestSource) {
      this._state.hitTestSource.cancel();
    }

    // Session-end owns the managed light's scene detach + GPU disposal.
    // XRLightProbeSystem.dispose() is defensive — it tolerates a null
    // state.managedLight — so a subsequent world teardown won't
    // double-dispose. Previously both paths called dispose(), emitting
    // Three.js warnings about a released resource being disposed twice.
    if (this._state.managedLight) {
      this._state.managedLight.removeFromParent();
      this._state.managedLight.dispose();
      this._state.managedLight = null;
    }

    // Clean up anchors
    this._state.anchorEntityMap.clear();
    this._state.anchorIdMap.clear();

    // Restore scene background if it was saved during AR session
    if (this._savedSceneBackground !== undefined) {
      this.app.world.scene.background = this._savedSceneBackground;
      this._savedSceneBackground = undefined;
    }

    // Remove session event listeners
    this._cleanupSession();

    // Clean up presentation backend
    if (this._presentationBackend) {
      this._presentationBackend.detachSession().catch(() => {});
      this._presentationBackend = null;
    }

    // Dispose layer manager (clears stale layers/binding from ended session)
    this._layerManager.dispose();

    // Cancel any in-flight haptic patterns so their setTimeout chains cannot
    // pulse into the next session's controllers when the state is reused.
    // Use optional chaining — `_haptics` is initialized in init(); if a session
    // ends synchronously during requestSession()'s async setup (e.g. a thrown
    // attachSession() is followed by session.end() and the 'end' event fires
    // before the catch resolves), `_haptics` may still be undefined.
    this._haptics?.dispose();

    // Reset renderer router
    this._rendererRouter.exitXR();

    // Reset runtime caps (keep gfx, clear xr)
    this._runtimeCaps = createDefaultRuntimeCaps(
      this.app.renderer.isWebGPU ? 'webgpu' : 'webgl2'
    );

    // Reset state
    this._state.reset();

    // Disable XR on renderer
    this.app.renderer.native.xr.enabled = false;

    this.app.events.emit('xr:session-ended', {});
  }

  private _handleVisibilityChange(): void {
    const state = this._state.session?.visibilityState ?? 'hidden';
    Logger.debug('XR', `Visibility: ${state}`);
    this.app.events.emit('xr:visibility-change', { state });
  }

  private _handleInputSourcesChange(event: XRInputSourcesChangeEvent): void {
    // Controller connect/disconnect is handled by XRControllerEntities
    // via the same event, but we also emit engine events here.
    for (const added of event.added) {
      const index = added.handedness === 'left' ? 0 : added.handedness === 'right' ? 1 : 2;
      this.app.events.emit('xr:controller-connected', {
        index,
        handedness: added.handedness,
        hasHand: !!added.hand,
      });
    }
    for (const removed of event.removed) {
      const index = removed.handedness === 'left' ? 0 : removed.handedness === 'right' ? 1 : 2;
      this.app.events.emit('xr:controller-disconnected', { index });
    }
  }

  private _cleanupSession(): void {
    const session = this._state.session;
    if (session) {
      if (this._onSessionEnd) session.removeEventListener('end', this._onSessionEnd);
      if (this._onVisibilityChange) session.removeEventListener('visibilitychange', this._onVisibilityChange);
      if (this._onInputSourcesChange) {
        session.removeEventListener('inputsourceschange', this._onInputSourcesChange as EventListener);
      }
    }
    this._onSessionEnd = null;
    this._onVisibilityChange = null;
    this._onInputSourcesChange = null;
  }

  private _onContextLost = (e: Event): void => {
    e.preventDefault();
    Logger.warn('XR', 'WebGL context lost — will restore');
  };

  private _onContextRestored = (): void => {
    Logger.info('XR', 'WebGL context restored');
  };
}
